import type { MapleEngine } from "../../../../../packages/engine-core/src";

import {
  AmbientLight,
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  BoxGeometry,
  BufferGeometry,
  CapsuleGeometry,
  Clock,
  Color,
  DoubleSide,
  CylinderGeometry,
  DirectionalLight,
  Fog,
  Group,
  HemisphereLight,
  Line,
  LineBasicMaterial,
  LineSegments,
  LoopOnce,
  LoopRepeat,
  Material,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  PointLight,
  Quaternion,
  Raycaster,
  RepeatWrapping,
  Scene,
  Sphere,
  SphereGeometry,
  TextureLoader,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Octree } from "three/examples/jsm/math/Octree.js";
import { Capsule } from "three/examples/jsm/math/Capsule.js";
import { deathmatchMode } from "./modes/dm";
import { EventBus, ObjectivesManager, type Objective } from "./scripting/api";
import type { CortexOptions, GameModeDefinition, LevelDefinition } from "./levels/types";
import { sampleBackgroundCamera } from "./ui/CameraRails";

const ASSET_BASE = "https://cdn.jsdelivr.net/gh/Footprintarts/ThreeJS_FPS_2.0@main/public";
const FPS_RIG_URL = `${ASSET_BASE}/models/FpsRig.glb`;
const SHOOT_AUDIO_URL = `${ASSET_BASE}/sounds/laser.mp3`;
const RELOAD_AUDIO_URL = `${ASSET_BASE}/sounds/reload.mp3`;
const AMBIENCE_AUDIO_URL = `${ASSET_BASE}/sounds/ambience1.mp3`;

type EnemyState = "patrol" | "engage";
type PickupType = "health" | "ammo";

export type CortexHudState = {
  health: number;
  shield: number;
  ammo: number;
  reserveAmmo: number;
  wave: number;
  score: number;
  enemiesRemaining: number;
  reloading: boolean;
  pointerLocked: boolean;
  sprint: number;
  status: string;
  weaponReady: boolean;
  heading: number;
  aiming: boolean;
  matchLabel: string;
  modeLabel: string;
  playersConnected: number;
  objectives: Objective[];
};

export interface CortexNetworkAdapterLike {
  getPlayers?: () => Array<{ id: string }>;
}

export interface CortexGameConfig {
  level: LevelDefinition;
  mode?: GameModeDefinition;
  missionId?: string;
  options: CortexOptions;
  networkAdapter?: CortexNetworkAdapterLike | null;
  backgroundMode?: boolean;
}

export interface CortexGameCallbacks {
  onHudChange?: (hud: CortexHudState) => void;
  onReadyChange?: (ready: boolean) => void;
  onGameOverChange?: (gameOver: boolean) => void;
  onPauseRequest?: () => void;
  onObjectivesChange?: (objectives: Objective[]) => void;
  isPaused?: () => boolean;
}

interface Enemy {
  id: number;
  root: Group;
  collider: Sphere;
  body: Mesh;
  head: Mesh;
  coreLight: PointLight;
  health: number;
  maxHealth: number;
  state: EnemyState;
  spawnPoint: Vector3;
  moveTarget: Vector3;
  attackCooldown: number;
  strafeSeed: number;
  hurtFlash: number;
}

interface EnemyProjectile {
  id: number;
  mesh: Mesh;
  velocity: Vector3;
  life: number;
  damage: number;
  radius: number;
}

interface Pickup {
  id: number;
  kind: PickupType;
  root: Group;
  radius: number;
  life: number;
}

interface Tracer {
  id: number;
  line: Line;
  life: number;
}

interface BulletImpact {
  id: number;
  mesh: Mesh;
  life: number;
  maxLife: number;
}

const UP = new Vector3(0, 1, 0);
const FORWARD = new Vector3(0, 0, 1);
const PLAYER_RADIUS = 0.35;
const MAGAZINE_SIZE = 30;
const MAX_RESERVE_AMMO = 180;

const tempVectorA = new Vector3();
const tempVectorB = new Vector3();
const tempVectorC = new Vector3();
const tempVectorD = new Vector3();
const tempVectorE = new Vector3();
const tempVector2 = new Vector2();
const tempQuaternion = new Quaternion();
const raycaster = new Raycaster();
const clipLookup = new Map<AnimationClip, string>();

const enemyBodyMaterial = new MeshStandardMaterial({ color: "#64748b", roughness: 0.7, metalness: 0.3 });
const enemyHeadMaterial = new MeshStandardMaterial({ color: "#f97316", roughness: 0.35, metalness: 0.1, emissive: "#f97316", emissiveIntensity: 0.6 });
const enemyProjectileMaterial = new MeshStandardMaterial({ color: "#f97316", emissive: "#f97316", emissiveIntensity: 1, roughness: 0.25, metalness: 0.05 });
const healthPickupMaterial = new MeshStandardMaterial({ color: "#22c55e", emissive: "#22c55e", emissiveIntensity: 0.4, roughness: 0.3 });
const ammoPickupMaterial = new MeshStandardMaterial({ color: "#38bdf8", emissive: "#38bdf8", emissiveIntensity: 0.35, roughness: 0.3 });
const tracerMaterial = new LineBasicMaterial({ color: "#fde68a", transparent: true, opacity: 0.8 });

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function tupleToVector3(tuple: [number, number, number]) {
  return new Vector3(tuple[0], tuple[1], tuple[2]);
}

function safePlay(audio: HTMLAudioElement) {
  audio.play().catch(() => undefined);
}

function playOneShot(url: string, volume: number) {
  const audio = new Audio(url);
  audio.volume = volume;
  safePlay(audio);
}

function disposeMaterial(material: Material | Material[]) {
  if (Array.isArray(material)) {
    material.forEach((entry) => entry.dispose());
    return;
  }
  material.dispose();
}

function disposeNode(root: Group | Scene) {
  root.traverse((child) => {
    if (child instanceof Mesh) {
      child.geometry.dispose();
      disposeMaterial(child.material);
    }
    if (child instanceof Line || child instanceof LineSegments) {
      child.geometry.dispose();
      disposeMaterial(child.material);
    }
  });
}

function findClip(clips: AnimationClip[], query: string) {
  const lowered = query.toLowerCase();
  return clips.find((clip) => {
    const key = clipLookup.get(clip) ?? clip.name;
    return key.includes(lowered);
  });
}

function createEnemy(spawnPoint: Vector3, id: number) {
  const root = new Group();
  root.position.copy(spawnPoint);

  const body = new Mesh(new CylinderGeometry(0.6, 0.85, 1.9, 12), enemyBodyMaterial.clone());
  body.castShadow = true;
  body.receiveShadow = true;
  body.position.y = 1.2;
  body.userData = { enemyId: id, hitZone: "body" };

  const head = new Mesh(new SphereGeometry(0.42, 16, 16), enemyHeadMaterial.clone());
  head.castShadow = true;
  head.position.y = 2.3;
  head.userData = { enemyId: id, hitZone: "head" };

  const armLeft = new Mesh(new BoxGeometry(0.25, 0.9, 1.1), enemyBodyMaterial.clone());
  armLeft.position.set(-0.9, 1.35, 0);
  armLeft.castShadow = true;

  const armRight = armLeft.clone();
  armRight.position.x = 0.9;

  const coreLight = new PointLight("#f97316", 1.4, 7, 2);
  coreLight.position.set(0, 1.8, 0.8);

  root.add(body, head, armLeft, armRight, coreLight);

  return {
    id,
    root,
    collider: new Sphere(root.position.clone().add(new Vector3(0, 1.3, 0)), 0.95),
    body,
    head,
    coreLight,
    health: 120,
    maxHealth: 120,
    state: "patrol" as EnemyState,
    spawnPoint: spawnPoint.clone(),
    moveTarget: spawnPoint.clone(),
    attackCooldown: Math.random() * 1.25,
    strafeSeed: Math.random() * Math.PI * 2,
    hurtFlash: 0,
  } satisfies Enemy;
}

function createPickup(kind: PickupType, position: Vector3, id: number) {
  const root = new Group();
  root.position.copy(position);

  const material = kind === "health" ? healthPickupMaterial.clone() : ammoPickupMaterial.clone();
  const base = new Mesh(new CylinderGeometry(0.42, 0.42, 0.24, 12), material);
  const top = new Mesh(new BoxGeometry(0.24, 0.9, 0.24), material.clone());
  top.rotation.z = Math.PI / 2;

  root.add(base);

  if (kind === "health") {
    const crossB = top.clone();
    crossB.rotation.z = 0;
    root.add(top, crossB);
  } else {
    const ammoBar = new Mesh(new BoxGeometry(0.9, 0.24, 0.24), material.clone());
    ammoBar.position.y = 0.45;
    root.add(ammoBar);
  }

  return {
    id,
    kind,
    root,
    radius: 1.15,
    life: 18,
  } satisfies Pickup;
}

function createTracer(start: Vector3, end: Vector3, id: number) {
  const geometry = new BufferGeometry().setFromPoints([start.clone(), end.clone()]);
  const line = new Line(geometry, tracerMaterial.clone());
  return {
    id,
    line,
    life: 0.08,
  } satisfies Tracer;
}

function createBulletImpact(position: Vector3, normal: Vector3, id: number) {
  const mesh = new Mesh(
    new PlaneGeometry(0.18, 0.18),
    new MeshBasicMaterial({
      color: "#111827",
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      side: DoubleSide,
    })
  );
  mesh.position.copy(position).addScaledVector(normal, 0.02);
  mesh.quaternion.copy(tempQuaternion.setFromUnitVectors(FORWARD, normal.clone().normalize()));
  return {
    id,
    mesh,
    life: 3.2,
    maxLife: 3.2,
  } satisfies BulletImpact;
}

function syncCameraToPlayer(camera: PerspectiveCamera, playerCollider: Capsule, bobOffset = 0) {
  const eye = playerCollider.end.clone();
  eye.y += bobOffset;
  camera.position.copy(eye);
}

function getPlayerCenter(playerCollider: Capsule) {
  return tempVectorA.copy(playerCollider.start).add(playerCollider.end).multiplyScalar(0.5);
}

export async function createCortexGame(engine: MapleEngine, config: CortexGameConfig, callbacks: CortexGameCallbacks = {}) {
  const canvas = engine.getCanvas();
  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.max(1, window.devicePixelRatio || 1));
  renderer.setSize(canvas.width, canvas.height, false);
  renderer.shadowMap.enabled = true;

  const scene = new Scene();
  scene.background = new Color(config.level.lighting?.backgroundColor ?? "#090d13");
  scene.fog = new Fog(config.level.lighting?.fogColor ?? "#090d13", config.level.lighting?.fogNear ?? 24, config.level.lighting?.fogFar ?? 68);

  const camera = new PerspectiveCamera(config.options.fov, canvas.width / canvas.height, 0.1, 160);
  camera.rotation.order = "YXZ";
  scene.add(camera);

  const ambient = new AmbientLight("#9ca3af", 0.45);
  const hemi = new HemisphereLight("#60a5fa", "#111827", 0.55);
  const sun = new DirectionalLight("#f8fafc", 1.45);
  sun.position.set(16, 28, 12);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 80;
  sun.shadow.camera.left = -28;
  sun.shadow.camera.right = 28;
  sun.shadow.camera.top = 28;
  sun.shadow.camera.bottom = -28;
  scene.add(ambient, hemi, sun);

  const worldOctree = new Octree();
  let levelRoot = new Group();
  let enemySpawns = config.level.spawnPoints.multiplayer.map((spawn) => spawn.position);
  const backgroundMode = Boolean(config.backgroundMode);

  if (config.level.gltfPath) {
    const gltf = await new GLTFLoader().loadAsync(config.level.gltfPath);
    levelRoot = gltf.scene;
    levelRoot.traverse((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    scene.add(levelRoot);
    worldOctree.fromGraphNode(levelRoot);
  } else if (config.level.proceduralFactory) {
    const result = await config.level.proceduralFactory({ scene, octree: worldOctree });
    levelRoot = result.levelRoot;
    enemySpawns = result.enemySpawns ?? enemySpawns;
  }

  const decalTexture = new TextureLoader().load("/os/assets/images/wallpapers/default.jpg");
  decalTexture.wrapS = RepeatWrapping;
  decalTexture.wrapT = RepeatWrapping;
  decalTexture.repeat.set(2, 2);

  const ceiling = new Mesh(
    new PlaneGeometry(42, 42),
    new MeshStandardMaterial({ map: decalTexture, color: "#0f172a", roughness: 0.95, metalness: 0.02 })
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = 11.9;
  ceiling.receiveShadow = true;
  scene.add(ceiling);

  const mode = config.mode ?? deathmatchMode;
  const spawnTuple = mode.id === "survival"
    ? (config.level.spawnPoints.singleplayer[0]?.position ?? [0, 2.8, 16])
    : (config.level.spawnPoints.multiplayer[0]?.position ?? [0, 2.8, 16]);
  const playerStart = tupleToVector3(spawnTuple);

  const playerCollider = new Capsule(
    playerStart.clone().add(new Vector3(0, 0.35, 0)),
    playerStart.clone().add(new Vector3(0, 1.75, 0)),
    PLAYER_RADIUS
  );
  const playerVelocity = new Vector3();
  const playerDirection = new Vector3();
  const cameraBobAnchor = new Vector3();
  const groundRaycaster = new Raycaster();

  let playerOnFloor = false;
  let pointerLocked = false;
  let firing = false;
  let rightMouseHeld = false;
  let queuedJump = false;
  let prevJump = false;
  let prevReload = false;
  let sprintEnergy = 100;
  let shotCooldown = 0;
  let reloadTimer = 0;
  let enemyIdSeed = 1;
  let projectileIdSeed = 1;
  let pickupIdSeed = 1;
  let tracerIdSeed = 1;
  let impactIdSeed = 1;
  let wave = 1;
  let score = 0;
  let health = 100;
  let shield = 35;
  let ammo = MAGAZINE_SIZE;
  let reserveAmmo = 120;
  let weaponReady = false;
  let isGameOver = false;
  let waveTimer = 0;
  let bobTime = 0;
  let boostTimer = 0;
  let hudPublishBudget = 0;
  let aimAmount = 0;
  let networkPublishBudget = 0;

  const clock = new Clock();
  const enemies: Enemy[] = [];
  const projectiles: EnemyProjectile[] = [];
  const pickups: Pickup[] = [];
  const tracers: Tracer[] = [];
  const impacts: BulletImpact[] = [];
  const muzzleFlash = new PointLight("#fde68a", 0, 6, 2);
  const weaponHolder = new Group();
  const weaponRestPosition = new Vector3(0.18, -0.22, -0.38);
  const weaponAimPosition = new Vector3(0.12, -0.19, -0.3);
  weaponHolder.position.copy(weaponRestPosition);
  if (!backgroundMode) {
    camera.add(weaponHolder);
    camera.add(muzzleFlash);
  }
  muzzleFlash.position.set(0.18, -0.08, -0.52);

  let weaponMixer: AnimationMixer | null = null;
  let weaponRoot: Group | null = null;
  let idleAction: AnimationAction | null = null;
  let shootAction: AnimationAction | null = null;
  let reloadAction: AnimationAction | null = null;
  let activeWeaponAction: AnimationAction | null = null;

  const ambience = new Audio(AMBIENCE_AUDIO_URL);
  ambience.loop = true;
  ambience.volume = config.options.masterVolume * config.options.musicVolume * (backgroundMode ? 0.32 : 1);

  const manualKeys = new Set<string>();
  const eventBus = new EventBus();
  const objectives = new ObjectivesManager();
  const missionState = new Map<string, unknown>();
  const activeVolumes = new Set<string>();

  let spawnWave = (_count: number) => undefined;

  const missionDefinition = backgroundMode ? undefined : (config.level.missions.find((mission) => mission.id === config.missionId) ?? config.level.missions[0]);
  const mission = backgroundMode ? undefined : missionDefinition?.createMission();

  const hud: CortexHudState = {
    health,
    shield,
    ammo,
    reserveAmmo,
    wave,
    score,
    enemiesRemaining: enemies.length,
    reloading: false,
    pointerLocked,
    sprint: sprintEnergy,
    status: backgroundMode ? `Ambient surveillance: ${config.level.name}` : `Loading ${config.level.name}...`,
    weaponReady,
    heading: 0,
    aiming: false,
    matchLabel: missionDefinition?.name ?? config.level.name,
    modeLabel: mode.shortLabel,
    playersConnected: config.networkAdapter?.getPlayers?.().length ?? 1,
    objectives: [],
  };

  const publishHud = () => {
    hud.health = Math.round(health);
    hud.shield = Math.round(shield);
    hud.ammo = ammo;
    hud.reserveAmmo = reserveAmmo;
    hud.wave = wave;
    hud.score = score;
    hud.enemiesRemaining = enemies.length;
    hud.reloading = reloadTimer > 0;
    hud.pointerLocked = backgroundMode ? false : pointerLocked;
    hud.sprint = Math.round(sprintEnergy);
    hud.weaponReady = backgroundMode ? false : weaponReady;
    hud.aiming = backgroundMode ? false : rightMouseHeld && pointerLocked && !isGameOver;
    hud.playersConnected = config.networkAdapter?.getPlayers?.().length ?? 1;
    hud.objectives = objectives.getAll();
    camera.getWorldDirection(tempVectorA);
    hud.heading = (Math.atan2(tempVectorA.x, -tempVectorA.z) * 180) / Math.PI;
    if (hud.heading < 0) {
      hud.heading += 360;
    }
    callbacks.onHudChange?.({ ...hud });
    callbacks.onObjectivesChange?.([...hud.objectives]);
  };

  const setStatus = (status: string) => {
    hud.status = status;
    publishHud();
  };

  const missionContext = {
    engine,
    scene,
    level: config.level,
    eventBus,
    objectives,
    setStatus,
    getState: <T,>(key: string, fallback: T) => (missionState.has(key) ? (missionState.get(key) as T) : fallback),
    setState: <T,>(key: string, value: T) => {
      missionState.set(key, value);
    },
    spawnWave: (count: number) => {
      spawnWave(count);
    },
    getEnemiesRemaining: () => enemies.length,
  };

  const missionUnsubscribe = eventBus.on("*", (event) => {
    mission?.onEvent?.(event, missionContext);
  });

  const objectivesUnsubscribe = objectives.subscribe((nextObjectives) => {
    hud.objectives = nextObjectives;
    callbacks.onObjectivesChange?.(nextObjectives);
    callbacks.onHudChange?.({ ...hud, objectives: nextObjectives });
  });

  const updateWeaponAction = (next: AnimationAction | null, loop = true, timeScale = 1) => {
    if (!next) return;
    if (activeWeaponAction && activeWeaponAction !== next) {
      activeWeaponAction.fadeOut(0.1);
    }
    next.enabled = true;
    next.reset();
    next.timeScale = timeScale;
    next.setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1);
    next.clampWhenFinished = !loop;
    next.fadeIn(0.08);
    next.play();
    activeWeaponAction = next;
  };

  const returnWeaponToIdle = () => {
    if (idleAction) {
      updateWeaponAction(idleAction, true, 1);
    }
  };

  const loadWeaponRig = async () => {
    const loader = new GLTFLoader();
    const gltf = await loader.loadAsync(FPS_RIG_URL);
    const root = gltf.scene;
    root.scale.setScalar(0.08);
    root.rotation.set(0.15, Math.PI / 2, 0.06);
    root.position.set(0, -0.05, -0.02);
    root.traverse((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    weaponRoot = root;
    weaponHolder.add(root);

    if (gltf.animations.length > 0) {
      weaponMixer = new AnimationMixer(root);
      gltf.animations.forEach((clip) => {
        clipLookup.set(clip, clip.name.toLowerCase());
      });
      idleAction = findClip(gltf.animations, "idle") ? weaponMixer.clipAction(findClip(gltf.animations, "idle")!) : null;
      shootAction = findClip(gltf.animations, "shoot") ? weaponMixer.clipAction(findClip(gltf.animations, "shoot")!) : null;
      reloadAction = findClip(gltf.animations, "reload") ? weaponMixer.clipAction(findClip(gltf.animations, "reload")!) : null;
      returnWeaponToIdle();
    }

    weaponReady = true;
    callbacks.onReadyChange?.(true);
    publishHud();
  };

  if (backgroundMode) {
    callbacks.onReadyChange?.(true);
    publishHud();
    safePlay(ambience);
  } else {
    try {
      await loadWeaponRig();
    } catch {
      const fallback = new Mesh(
        new BoxGeometry(0.18, 0.18, 0.72),
        new MeshStandardMaterial({ color: "#94a3b8", roughness: 0.38, metalness: 0.45 })
      );
      fallback.position.set(0.16, -0.05, -0.2);
      weaponHolder.add(fallback);
      weaponReady = true;
      callbacks.onReadyChange?.(true);
      publishHud();
    }
  }

  spawnWave = (count: number) => {
    while (enemies.length > 0) {
      const enemy = enemies.pop();
      if (!enemy) continue;
      scene.remove(enemy.root);
      disposeNode(enemy.root);
    }

    const shuffledSpawns = [...enemySpawns].sort(() => Math.random() - 0.5);
    for (let i = 0; i < count; i += 1) {
      const spawn = tupleToVector3(shuffledSpawns[i % shuffledSpawns.length] ?? [0, 1.2, 0]);
      const jitter = new Vector3(MathUtils.randFloatSpread(2.5), 0, MathUtils.randFloatSpread(2.5));
      const enemy = createEnemy(spawn.add(jitter), enemyIdSeed++);
      enemy.moveTarget = enemy.spawnPoint.clone().add(new Vector3(MathUtils.randFloatSpread(6), 0, MathUtils.randFloatSpread(6)));
      enemies.push(enemy);
      scene.add(enemy.root);
    }
    eventBus.emit("wave:started", { wave, count });
    publishHud();
  };

  if (!backgroundMode) {
    mission?.init?.(missionContext);
    mission?.onStart?.(missionContext);
    spawnWave(mode.id === "survival" ? 4 : 0);
    if (mode.id !== "survival") {
      setStatus(`${mode.name} staged on ${config.level.name}. Waiting for combat contact.`);
    }
  }

  const setGameOver = (nextGameOver: boolean, status: string) => {
    isGameOver = nextGameOver;
    setStatus(status);
    callbacks.onGameOverChange?.(nextGameOver);
    if (nextGameOver && !backgroundMode) {
      document.exitPointerLock?.();
    }
  };

  const playerCollisions = () => {
    const result = worldOctree.capsuleIntersect(playerCollider);
    playerOnFloor = false;

    if (!result) return;

    playerOnFloor = result.normal.y > 0;

    if (!playerOnFloor) {
      playerVelocity.addScaledVector(result.normal, -result.normal.dot(playerVelocity));
    }

    playerCollider.translate(result.normal.multiplyScalar(result.depth));
  };

  const hasGroundContact = () => {
    groundRaycaster.set(playerCollider.start.clone().add(new Vector3(0, 0.2, 0)), new Vector3(0, -1, 0));
    const hits = groundRaycaster.intersectObjects(levelRoot.children, true);
    return hits.length > 0 && hits[0].distance <= 0.7;
  };

  const updateTriggerVolumes = () => {
    const playerPosition = getPlayerCenter(playerCollider).clone();

    for (const trigger of config.level.triggerVolumes) {
      const [cx, cy, cz] = trigger.center;
      const [sx, sy, sz] = trigger.size;
      const inside =
        Math.abs(playerPosition.x - cx) <= sx / 2 &&
        Math.abs(playerPosition.y - cy) <= sy / 2 &&
        Math.abs(playerPosition.z - cz) <= sz / 2;

      const active = activeVolumes.has(trigger.id);

      if (inside && !active) {
        activeVolumes.add(trigger.id);
        eventBus.emit("trigger:enter", { id: trigger.id, event: trigger.event });
        eventBus.emit(trigger.event, { id: trigger.id });
      }

      if (!inside && active) {
        activeVolumes.delete(trigger.id);
        eventBus.emit("trigger:exit", { id: trigger.id, event: trigger.event });
      }
    }
  };

  const updatePlayer = (dt: number) => {
    const runPressed = ["ShiftLeft", "ShiftRight", "Shift"].some((key) => manualKeys.has(key) || engine.input.isKeyDown(key));
    const jumpPressed = ["Space", " ", "Spacebar"].some((key) => manualKeys.has(key) || engine.input.isKeyDown(key));
    const jumpTriggered = queuedJump || (jumpPressed && !prevJump);
    queuedJump = false;
    prevJump = jumpPressed;
    const isReloadPressed = ["KeyR", "r", "R"].some((key) => manualKeys.has(key) || engine.input.isKeyDown(key));
    const moveForwardPressed = ["KeyW", "w", "W"].some((key) => manualKeys.has(key) || engine.input.isKeyDown(key));
    const moveBackPressed = ["KeyS", "s", "S"].some((key) => manualKeys.has(key) || engine.input.isKeyDown(key));
    const moveLeftPressed = ["KeyA", "a", "A"].some((key) => manualKeys.has(key) || engine.input.isKeyDown(key));
    const moveRightPressed = ["KeyD", "d", "D"].some((key) => manualKeys.has(key) || engine.input.isKeyDown(key));
    const isMoving = moveForwardPressed || moveBackPressed || moveLeftPressed || moveRightPressed;

    if (!pointerLocked || isGameOver || callbacks.isPaused?.()) {
      playerVelocity.x = MathUtils.damp(playerVelocity.x, 0, 10, dt);
      playerVelocity.z = MathUtils.damp(playerVelocity.z, 0, 10, dt);
      return;
    }

    shotCooldown = Math.max(0, shotCooldown - dt);
    reloadTimer = Math.max(0, reloadTimer - dt);
    if (reloadTimer === 0 && hud.reloading) {
      const needed = MAGAZINE_SIZE - ammo;
      const refill = Math.min(needed, reserveAmmo);
      ammo += refill;
      reserveAmmo -= refill;
      returnWeaponToIdle();
      setStatus(`Magazine reloaded. ${missionDefinition?.name ?? "Combat run"} remains hot.`);
      publishHud();
    }

    if (isReloadPressed && !prevReload && ammo < MAGAZINE_SIZE && reserveAmmo > 0 && reloadTimer <= 0) {
      reloadTimer = 1.55;
      playOneShot(RELOAD_AUDIO_URL, config.options.masterVolume * config.options.sfxVolume * 0.45);
      if (reloadAction) {
        updateWeaponAction(reloadAction, false, 1.25);
      }
      setStatus("Reloading weapon rig...");
    }
    prevReload = isReloadPressed;

    const damping = Math.exp(-7 * dt) - 1;
    if (!playerOnFloor) {
      playerVelocity.y -= 28 * dt;
      playerVelocity.addScaledVector(playerVelocity, damping * 0.15);
    } else {
      playerVelocity.addScaledVector(playerVelocity, damping);
    }

    const isSprinting = runPressed && sprintEnergy > 0 && isMoving;
    const moveSpeed = isSprinting ? 107.5 : 67.5;
    if (isSprinting) {
      sprintEnergy = clamp(sprintEnergy - dt * 22, 0, 100);
      boostTimer = 0.12;
    } else {
      sprintEnergy = clamp(sprintEnergy + dt * 12, 0, 100);
    }

    camera.getWorldDirection(playerDirection);
    playerDirection.y = 0;
    playerDirection.normalize();
    const side = tempVectorB.crossVectors(playerDirection, UP).normalize();
    const move = tempVectorC.set(0, 0, 0);

    if (moveForwardPressed) move.add(playerDirection);
    if (moveBackPressed) move.sub(playerDirection);
    if (moveLeftPressed) move.sub(side);
    if (moveRightPressed) move.add(side);

    if (move.lengthSq() > 0) {
      move.normalize();
      playerVelocity.addScaledVector(move, moveSpeed * (playerOnFloor ? 1 : 0.35) * dt);

      const horizontalVelocity = tempVectorE.set(playerVelocity.x, 0, playerVelocity.z);
      const maxHorizontalSpeed = isSprinting ? 47 : 31;
      if (horizontalVelocity.length() > maxHorizontalSpeed) {
        horizontalVelocity.setLength(maxHorizontalSpeed);
        playerVelocity.x = horizontalVelocity.x;
        playerVelocity.z = horizontalVelocity.z;
      }

      bobTime += dt * (isSprinting ? 12 : 8);
    } else {
      bobTime = MathUtils.damp(bobTime, 0, 8, dt);
    }

    if (!playerOnFloor && hasGroundContact()) {
      playerOnFloor = true;
    }

    if (playerOnFloor && jumpTriggered) {
      playerVelocity.y = 13.8;
      playerOnFloor = false;
    }

    const deltaPosition = tempVectorD.copy(playerVelocity).multiplyScalar(dt);
    playerCollider.translate(deltaPosition);
    playerCollisions();

    const feet = playerCollider.start.y;
    if (feet < -6) {
      setGameOver(true, "Signal lost. Cortex collapsed the run.");
    }

    const bobOffset = Math.sin(bobTime) * 0.03 + Math.cos(bobTime * 0.5) * 0.015;
    syncCameraToPlayer(camera, playerCollider, bobOffset);

    if (weaponRoot) {
      const swayX = Math.sin(bobTime) * MathUtils.lerp(0.018, 0.006, aimAmount);
      const swayY = Math.abs(Math.cos(bobTime * 0.5)) * MathUtils.lerp(0.02, 0.007, aimAmount);
      weaponHolder.position.lerp(
        tempVectorE.copy(weaponRestPosition).lerp(weaponAimPosition, aimAmount).add(tempVectorA.set(swayX, swayY, 0)),
        MathUtils.lerp(0.18, 0.28, aimAmount)
      );
    }

    if (!hud.reloading && firing && shotCooldown <= 0 && ammo > 0) {
      raycaster.setFromCamera(tempVector2.set(0, 0), camera);
      const enemyHits = raycaster.intersectObjects(enemies.flatMap((enemy) => [enemy.body, enemy.head]), false);
      const worldHits = raycaster.intersectObjects(levelRoot.children, true);
      const shotOrigin = raycaster.ray.origin.clone().add(raycaster.ray.direction.clone().multiplyScalar(0.15));
      let hitPoint = raycaster.ray.origin.clone().add(raycaster.ray.direction.clone().multiplyScalar(60));
      const nearestEnemyHit = enemyHits[0] ?? null;
      const nearestWorldHit = worldHits[0] ?? null;
      const didHitWorldFirst = Boolean(nearestWorldHit && (!nearestEnemyHit || nearestWorldHit.distance <= nearestEnemyHit.distance));

      if (didHitWorldFirst && nearestWorldHit) {
        hitPoint = nearestWorldHit.point.clone();
        const impact = createBulletImpact(hitPoint, nearestWorldHit.face?.normal?.clone().transformDirection(nearestWorldHit.object.matrixWorld) ?? UP, impactIdSeed++);
        impacts.push(impact);
        scene.add(impact.mesh);
      } else if (nearestEnemyHit) {
        hitPoint = nearestEnemyHit.point.clone();
        const enemyId = nearestEnemyHit.object.userData.enemyId as number;
        const enemy = enemies.find((entry) => entry.id === enemyId);
        if (enemy) {
          const hitZone = nearestEnemyHit.object.userData.hitZone === "head" ? "head" : "body";
          const damage = hitZone === "head" ? 52 : 26;
          enemy.health -= damage;
          enemy.hurtFlash = 0.16;
          enemy.state = "engage";
          enemy.moveTarget.copy(getPlayerCenter(playerCollider));
          setStatus(hitZone === "head" ? "Critical hit on Cortex sentry core." : "Target impact confirmed.");
          if (enemy.health <= 0) {
            score += 125 + wave * 25;
            scene.remove(enemy.root);
            disposeNode(enemy.root);
            const index = enemies.indexOf(enemy);
            if (index >= 0) enemies.splice(index, 1);
            eventBus.emit("enemy:killed", { enemyId, wave, hitZone });

            if (Math.random() < 0.45) {
              const kind: PickupType = Math.random() < 0.5 ? "health" : "ammo";
              const pickup = createPickup(kind, enemy.collider.center.clone(), pickupIdSeed++);
              pickups.push(pickup);
              scene.add(pickup.root);
            }
          } else {
            score += hitZone === "head" ? 30 : 15;
          }
        }
      }

      const tracer = createTracer(shotOrigin, hitPoint, tracerIdSeed++);
      tracers.push(tracer);
      scene.add(tracer.line);
      playOneShot(SHOOT_AUDIO_URL, config.options.masterVolume * config.options.sfxVolume * 0.22);
      muzzleFlash.intensity = 2.75;
      if (shootAction) {
        updateWeaponAction(shootAction, false, 1.4);
      }
      ammo -= 1;
      shotCooldown = rightMouseHeld ? 0.2 : 0.085;

      if (ammo <= 0 && reserveAmmo > 0) {
        reloadTimer = 1.55;
        playOneShot(RELOAD_AUDIO_URL, config.options.masterVolume * config.options.sfxVolume * 0.38);
        if (reloadAction) {
          updateWeaponAction(reloadAction, false, 1.25);
        }
        setStatus("Magazine empty. Auto-reload started.");
      }
    }
  };

  const updateEnemies = (dt: number) => {
    if (mode.id !== "survival") {
      return;
    }

    const playerPosition = getPlayerCenter(playerCollider).clone();

    for (const enemy of enemies) {
      enemy.collider.center.copy(enemy.root.position).add(new Vector3(0, 1.25, 0));
      const toPlayer = tempVectorA.copy(playerPosition).sub(enemy.root.position);
      const distanceToPlayer = toPlayer.length();
      enemy.hurtFlash = Math.max(0, enemy.hurtFlash - dt);
      enemy.attackCooldown = Math.max(0, enemy.attackCooldown - dt);

      const bodyMaterial = enemy.body.material as MeshStandardMaterial;
      const headMaterial = enemy.head.material as MeshStandardMaterial;
      bodyMaterial.color.set(enemy.hurtFlash > 0 ? "#fca5a5" : "#64748b");
      headMaterial.emissiveIntensity = enemy.hurtFlash > 0 ? 1.1 : 0.6;
      enemy.coreLight.intensity = enemy.hurtFlash > 0 ? 2.2 : 1.4;

      if (distanceToPlayer < 18 || enemy.health < enemy.maxHealth) {
        enemy.state = "engage";
      }

      if (enemy.state === "engage") {
        const preferredDistance = 10 + Math.sin(enemy.strafeSeed + performance.now() * 0.001) * 2;
        const moveDir = tempVectorB.copy(toPlayer).normalize();
        const strafe = tempVectorC.crossVectors(moveDir, UP).normalize().multiplyScalar(Math.sin(performance.now() * 0.002 + enemy.strafeSeed));
        const desired = tempVectorD.set(0, 0, 0);

        if (distanceToPlayer > preferredDistance + 1.5) {
          desired.add(moveDir);
        } else if (distanceToPlayer < preferredDistance - 1.5) {
          desired.sub(moveDir);
        }
        desired.addScaledVector(strafe, 0.8).normalize();

        enemy.root.position.addScaledVector(desired, dt * (2.6 + wave * 0.08));
        enemy.root.position.x = clamp(enemy.root.position.x, -22, 22);
        enemy.root.position.z = clamp(enemy.root.position.z, -22, 22);
        enemy.root.lookAt(playerPosition.x, enemy.root.position.y + 1.25, playerPosition.z);

        if (enemy.attackCooldown <= 0 && distanceToPlayer < 22 && !isGameOver) {
          const projectile = {
            id: projectileIdSeed++,
            mesh: new Mesh(new SphereGeometry(0.18, 12, 12), enemyProjectileMaterial.clone()),
            velocity: tempVectorE.copy(toPlayer).normalize().multiplyScalar(8 + wave * 0.35),
            life: 3.5,
            damage: 9 + wave,
            radius: 0.22,
          } satisfies EnemyProjectile;
          projectile.mesh.position.copy(enemy.root.position).add(new Vector3(0, 1.6, 0));
          projectile.mesh.castShadow = true;
          scene.add(projectile.mesh);
          projectiles.push(projectile);
          enemy.attackCooldown = MathUtils.randFloat(1.05, 1.85);
        }
      } else {
        if (enemy.root.position.distanceTo(enemy.moveTarget) < 0.85) {
          enemy.moveTarget.copy(enemy.spawnPoint).add(new Vector3(MathUtils.randFloatSpread(8), 0, MathUtils.randFloatSpread(8)));
        }
        tempVectorB.copy(enemy.moveTarget).sub(enemy.root.position).normalize();
        enemy.root.position.addScaledVector(tempVectorB, dt * 1.75);
        enemy.root.lookAt(enemy.moveTarget.x, enemy.root.position.y + 1.25, enemy.moveTarget.z);
      }
    }

    if (enemies.length === 0 && !isGameOver) {
      waveTimer += dt;
      if (waveTimer === dt) {
        setStatus(`Arena clear. Cortex wave ${wave + 1} inbound...`);
        eventBus.emit("wave:clear-pending", { nextWave: wave + 1 });
      }
      if (waveTimer > 2.6) {
        wave += 1;
        waveTimer = 0;
        spawnWave(Math.min(4 + wave, 10));
      }
    } else {
      waveTimer = 0;
    }
  };

  const updateProjectiles = (dt: number) => {
    const playerCenter = getPlayerCenter(playerCollider).clone();

    for (let i = projectiles.length - 1; i >= 0; i -= 1) {
      const projectile = projectiles[i];
      projectile.life -= dt;
      projectile.mesh.position.addScaledVector(projectile.velocity, dt);

      const hitWorld = worldOctree.sphereIntersect(new Sphere(projectile.mesh.position.clone(), projectile.radius));
      const hitPlayer = projectile.mesh.position.distanceTo(playerCenter) < projectile.radius + 0.95;

      if (hitPlayer && !isGameOver) {
        const shieldDamage = Math.min(shield, projectile.damage * 0.65);
        shield -= shieldDamage;
        health -= projectile.damage - shieldDamage;
        setStatus("Cortex sentry return fire detected.");
        eventBus.emit("player:damaged", { amount: projectile.damage });
        if (health <= 0) {
          health = 0;
          setGameOver(true, "Cortex overwhelmed the rig. Run failed.");
        }
      }

      if (projectile.life <= 0 || hitWorld || hitPlayer) {
        scene.remove(projectile.mesh);
        projectile.mesh.geometry.dispose();
        disposeMaterial(projectile.mesh.material);
        projectiles.splice(i, 1);
      }
    }
  };

  const updatePickups = (dt: number) => {
    const playerCenter = getPlayerCenter(playerCollider).clone();

    for (let i = pickups.length - 1; i >= 0; i -= 1) {
      const pickup = pickups[i];
      pickup.life -= dt;
      pickup.root.rotation.y += dt * 1.2;
      pickup.root.position.y += Math.sin(performance.now() * 0.004 + pickup.id) * 0.002;

      if (pickup.root.position.distanceTo(playerCenter) < pickup.radius && !isGameOver) {
        if (pickup.kind === "health") {
          health = clamp(health + 28, 0, 100);
          shield = clamp(shield + 12, 0, 50);
          setStatus("Recovery cache secured.");
        } else {
          reserveAmmo = clamp(reserveAmmo + 36, 0, MAX_RESERVE_AMMO);
          setStatus("Ammo cache recovered.");
        }
        eventBus.emit("pickup:collected", { kind: pickup.kind });
        scene.remove(pickup.root);
        disposeNode(pickup.root);
        pickups.splice(i, 1);
        continue;
      }

      if (pickup.life <= 0) {
        scene.remove(pickup.root);
        disposeNode(pickup.root);
        pickups.splice(i, 1);
      }
    }
  };

  const updateTracers = (dt: number) => {
    for (let i = tracers.length - 1; i >= 0; i -= 1) {
      const tracer = tracers[i];
      tracer.life -= dt;
      const material = tracer.line.material as LineBasicMaterial;
      material.opacity = clamp(tracer.life / 0.08, 0, 0.8);
      if (tracer.life <= 0) {
        scene.remove(tracer.line);
        tracer.line.geometry.dispose();
        material.dispose();
        tracers.splice(i, 1);
      }
    }
  };

  const updateImpacts = (dt: number) => {
    for (let i = impacts.length - 1; i >= 0; i -= 1) {
      const impact = impacts[i];
      impact.life -= dt;
      const material = impact.mesh.material as MeshBasicMaterial;
      material.opacity = 0.7 * clamp(impact.life / impact.maxLife, 0, 1);
      if (impact.life <= 0) {
        scene.remove(impact.mesh);
        impact.mesh.geometry.dispose();
        material.dispose();
        impacts.splice(i, 1);
      }
    }
  };

  const updateBackgroundCamera = (elapsed: number, dt: number) => {
    const pose = sampleBackgroundCamera(config.level, elapsed);
    camera.position.lerp(pose.position, 1 - Math.exp(-dt * 1.4));
    camera.lookAt(pose.lookAt);
    camera.fov = MathUtils.damp(camera.fov, config.options.fov - 6, 4, dt);
    camera.updateProjectionMatrix();
  };

  const mouseMove = (event: MouseEvent) => {
    if (!pointerLocked || isGameOver || callbacks.isPaused?.()) return;
    const lookSensitivity = (rightMouseHeld ? 0.0013 : 0.00225) * config.options.mouseSensitivity;
    camera.rotation.y -= event.movementX * lookSensitivity;
    const invertFactor = config.options.invertY ? -1 : 1;
    camera.rotation.x -= event.movementY * lookSensitivity * invertFactor;
    camera.rotation.x = clamp(camera.rotation.x, -Math.PI / 2.1, Math.PI / 2.1);
  };

  const keyDown = (event: KeyboardEvent) => {
    manualKeys.add(event.code);
    manualKeys.add(event.key);

    if (event.code === "Escape") {
      callbacks.onPauseRequest?.();
      document.exitPointerLock?.();
      event.preventDefault();
      return;
    }

    const isSpaceKey = event.code === "Space" || event.key === " " || event.key === "Spacebar";
    if (isSpaceKey) {
      queuedJump = true;

      if (!event.repeat && pointerLocked && !isGameOver && !callbacks.isPaused?.() && (playerOnFloor || hasGroundContact())) {
        playerVelocity.y = Math.max(playerVelocity.y, 13.8);
        playerCollider.translate(new Vector3(0, 0.04, 0));
        playerOnFloor = false;
        queuedJump = false;
        prevJump = true;
      }
    }

    if (pointerLocked && ["Space", "ShiftLeft", "ShiftRight", "KeyW", "KeyA", "KeyS", "KeyD", "KeyR"].includes(event.code)) {
      event.preventDefault();
    }
  };

  const keyUp = (event: KeyboardEvent) => {
    manualKeys.delete(event.code);
    manualKeys.delete(event.key);
  };

  const clearKeys = () => {
    manualKeys.clear();
    prevJump = false;
    prevReload = false;
    queuedJump = false;
  };

  const mouseDown = (event: MouseEvent) => {
    if (callbacks.isPaused?.()) {
      return;
    }

    if (event.button === 0) {
      if (!pointerLocked) {
        canvas.requestPointerLock?.();
        safePlay(ambience);
        return;
      }
      firing = true;
    }

    if (event.button === 2 && pointerLocked) {
      rightMouseHeld = true;
    }
  };

  const mouseUp = (event: MouseEvent) => {
    if (event.button === 0) firing = false;
    if (event.button === 2) rightMouseHeld = false;
  };

  const handlePointerLock = () => {
    pointerLocked = document.pointerLockElement === canvas && !callbacks.isPaused?.();
    if (pointerLocked) {
      safePlay(ambience);
      setStatus(`${config.level.name} live. Sweep the arena.`);
    } else if (!isGameOver && !callbacks.isPaused?.()) {
      setStatus("Pointer released. Click the viewport to re-engage.");
    }
    publishHud();
  };

  const suppressContext = (event: MouseEvent) => {
    event.preventDefault();
  };

  if (!backgroundMode) {
    document.addEventListener("mousemove", mouseMove);
    window.addEventListener("keydown", keyDown, { passive: false });
    window.addEventListener("keyup", keyUp);
    window.addEventListener("blur", clearKeys);
    canvas.addEventListener("mousedown", mouseDown);
    document.addEventListener("mouseup", mouseUp);
    document.addEventListener("pointerlockchange", handlePointerLock);
    canvas.addEventListener("contextmenu", suppressContext);
  }

  const resizeSystem = {
    update() {
      if (camera.aspect !== canvas.width / Math.max(canvas.height, 1)) {
        renderer.setSize(canvas.width, canvas.height, false);
        camera.aspect = canvas.width / Math.max(canvas.height, 1);
        camera.updateProjectionMatrix();
      }
    },
  };

  const simulationSystem = {
    update(dt: number) {
      const elapsed = clock.getElapsedTime();

      if (callbacks.isPaused?.() && !backgroundMode) {
        pointerLocked = false;
        publishHud();
        return;
      }

      if (backgroundMode) {
        updateBackgroundCamera(elapsed, dt);
        hudPublishBudget -= dt;
        if (hudPublishBudget <= 0) {
          hudPublishBudget = 0.12;
          publishHud();
        }
        return;
      }

      boostTimer = Math.max(0, boostTimer - dt);
      muzzleFlash.intensity = MathUtils.damp(muzzleFlash.intensity, 0, 18, dt);

      updatePlayer(dt);
      updateEnemies(dt);
      updateProjectiles(dt);
      updatePickups(dt);
      updateTracers(dt);
      updateImpacts(dt);
      updateTriggerVolumes();
      mission?.onUpdate?.(dt, missionContext);

      if (weaponMixer) {
        weaponMixer.update(dt);
      }

      if (activeWeaponAction && activeWeaponAction !== idleAction && activeWeaponAction.time >= activeWeaponAction.getClip().duration - 0.02 && reloadTimer <= 0) {
        returnWeaponToIdle();
      }

      const targetFov = rightMouseHeld && pointerLocked && !isGameOver ? config.options.fov - 26 : config.options.fov;
      camera.fov = MathUtils.damp(camera.fov, targetFov, 10, dt);
      camera.updateProjectionMatrix();

      aimAmount = MathUtils.damp(aimAmount, rightMouseHeld && pointerLocked && !isGameOver ? 1 : 0, 10, dt);

      if (weaponRoot) {
        const targetWeaponTilt = boostTimer > 0 ? 0.1 : 0.06;
        weaponRoot.rotation.z = MathUtils.damp(
          weaponRoot.rotation.z,
          MathUtils.lerp(targetWeaponTilt, targetWeaponTilt * 0.35, aimAmount),
          MathUtils.lerp(6, 10, aimAmount),
          dt
        );
      }

      cameraBobAnchor.set(Math.sin(elapsed * 0.9) * 0.002, 0, 0);
      camera.position.add(cameraBobAnchor);

      networkPublishBudget -= dt;
      if (networkPublishBudget <= 0) {
        networkPublishBudget = 0.1;
      }

      hudPublishBudget -= dt;
      if (hudPublishBudget <= 0) {
        hudPublishBudget = 0.016;
        publishHud();
      }
    },
  };

  const renderSystem = {
    update() {
      renderer.render(scene, camera);
    },
  };

  if (backgroundMode) {
    const initialPose = sampleBackgroundCamera(config.level, 0);
    camera.position.copy(initialPose.position);
    camera.lookAt(initialPose.lookAt);
  } else {
    syncCameraToPlayer(camera, playerCollider, 0);
  }
  engine.addSystem(resizeSystem);
  engine.addSystem(simulationSystem);
  engine.addSystem(renderSystem);
  setStatus(backgroundMode ? `Ambient surveillance online for ${config.level.name}.` : mode.id === "survival" ? `Mission ready: ${missionDefinition?.name ?? config.level.name}` : `Match ready: ${mode.name}`);

  return () => {
    firing = false;
    rightMouseHeld = false;
    missionUnsubscribe();
    objectivesUnsubscribe();
    if (!backgroundMode) {
      document.removeEventListener("mousemove", mouseMove);
      window.removeEventListener("keydown", keyDown as EventListener);
      window.removeEventListener("keyup", keyUp as EventListener);
      window.removeEventListener("blur", clearKeys);
      canvas.removeEventListener("mousedown", mouseDown);
      document.removeEventListener("mouseup", mouseUp);
      document.removeEventListener("pointerlockchange", handlePointerLock);
      canvas.removeEventListener("contextmenu", suppressContext);
      document.exitPointerLock?.();
    }
    ambience.pause();
    ambience.currentTime = 0;
    callbacks.onReadyChange?.(false);
    callbacks.onGameOverChange?.(false);
    disposeNode(scene);
    renderer.dispose();
  };
}